import { Component, OnInit, Inject } from '@angular/core';
import { NbDialogRef, NB_DIALOG_CONFIG } from '@nebular/theme';

export interface ColorOption {
  code: string;
  name?: string;
}

@Component({
  selector: 'app-color-picker',
  template: `
    <div class="color-picker-overlay" (click)="closePicker()">
      <div class="color-picker-modal" (click)="$event.stopPropagation()">
        <div class="color-picker-header">
          <h3 class="color-picker-title">{{ title }}</h3>
          <button class="close-btn" (click)="closePicker()" type="button">
            <span>&times;</span>
          </button>
        </div>
        
        <div class="color-picker-description" *ngIf="description">
          <p>{{ description }}</p>
        </div>

        <div class="color-picker-content">
          <div class="color-tabs">
            <button 
              class="tab-btn" 
              [class.active]="activeTab === 'primary'"
              (click)="setActiveTab('primary')">
              Primary
            </button>
            <button 
              class="tab-btn" 
              [class.active]="activeTab === 'accent'"
              (click)="setActiveTab('accent')">
              Accent
            </button>
            <button 
              class="tab-btn" 
              [class.active]="activeTab === 'history'"
              (click)="setActiveTab('history')">
              History
            </button>
          </div>

          <div class="color-palette" *ngIf="activeTab === 'primary'">
            <div class="color-row">
              <div 
                *ngFor="let color of primaryColors" 
                class="color-swatch"
                [style.background-color]="color.code"
                [class.selected]="selectedColor === color.code"
                (click)="selectColor(color.code)"
                [title]="color.name || color.code">
              </div>
            </div>
            <div class="color-row">
              <div 
                *ngFor="let color of primaryColors2" 
                class="color-swatch"
                [style.background-color]="color.code"
                [class.selected]="selectedColor === color.code"
                (click)="selectColor(color.code)"
                [title]="color.name || color.code">
              </div>
            </div>
          </div>

          <div class="color-palette" *ngIf="activeTab === 'accent'">
            <div class="color-row">
              <div 
                *ngFor="let color of accentColors" 
                class="color-swatch"
                [style.background-color]="color.code"
                [class.selected]="selectedColor === color.code"
                (click)="selectColor(color.code)"
                [title]="color.name || color.code">
              </div>
            </div>
          </div>

          <div class="color-palette" *ngIf="activeTab === 'history'">
            <div class="history-header">
              <span>Recently Used Colors</span>
              <button 
                type="button" 
                class="clear-history-btn" 
                (click)="clearHistory()"
                title="Clear History">
                Clear
              </button>
            </div>
            <div class="color-row" *ngIf="recentColors.length > 0">
              <div 
                *ngFor="let color of recentColors" 
                class="color-swatch"
                [style.background-color]="color"
                [class.selected]="selectedColor === color"
                (click)="selectColor(color)"
                [title]="'Recently used: ' + color">
              </div>
            </div>
            <div class="no-history" *ngIf="recentColors.length === 0">
              <p>No colors in history yet. Start selecting colors to build your history!</p>
            </div>
          </div>

          <div class="custom-color-section">
            <label for="custom-color">Custom Color:</label>
            <input 
              type="color" 
              id="custom-color"
              [(ngModel)]="customColor"
              (change)="selectColor(customColor)"
              class="custom-color-input">
            <span class="custom-color-value">{{ customColor }}</span>
          </div>
        </div>

        <div class="color-picker-footer">
          <button 
            class="select-btn" 
            (click)="confirmSelection()"
            [disabled]="!selectedColor">
            Select
          </button>
          <button class="cancel-btn" (click)="closePicker()">
            Cancel
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .color-picker-overlay {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 400px;
      padding: 20px;
    }

    .color-picker-modal {
      background-color: #2a2a2a;
      border-radius: 8px;
      padding: 20px;
      width: 100%;
      max-width: 500px;
      min-width: 320px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
      max-height: 90vh;
      overflow-y: auto;
    }

    .color-picker-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
    }

    .color-picker-title {
      margin: 0;
      color: #ffffff;
      font-size: 18px;
      font-weight: 600;
    }

    .close-btn {
      background: none;
      border: none;
      color: #ffffff;
      font-size: 24px;
      cursor: pointer;
      padding: 0;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s;
    }

    .close-btn:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }

    .color-picker-description {
      margin-bottom: 20px;
    }

    .color-picker-description p {
      color: #cccccc;
      margin: 0;
      font-size: 14px;
      line-height: 1.4;
    }

    .color-tabs {
      display: flex;
      margin-bottom: 20px;
      border-bottom: 1px solid #444;
      flex-wrap: wrap;
      gap: 5px;
    }

    .tab-btn {
      background: none;
      border: none;
      color: #cccccc;
      padding: 10px 16px;
      cursor: pointer;
      font-size: 14px;
      border-bottom: 2px solid transparent;
      transition: all 0.2s;
      flex: 1;
      min-width: 80px;
      text-align: center;
      white-space: nowrap;
    }

    .tab-btn:hover {
      color: #ffffff;
      background-color: rgba(255, 255, 255, 0.05);
    }

    .tab-btn.active {
      color: #ffffff;
      border-bottom-color: #4CAF50;
      background-color: rgba(76, 175, 80, 0.1);
    }

    .color-palette {
      margin-bottom: 20px;
    }

    .color-row {
      display: flex;
      gap: 8px;
      margin-bottom: 8px;
      justify-content: center;
      flex-wrap: wrap;
    }

    .color-swatch {
      width: 32px;
      height: 32px;
      border-radius: 4px;
      cursor: pointer;
      border: 2px solid transparent;
      transition: all 0.2s;
      position: relative;
      flex-shrink: 0;
    }

    .color-swatch:hover {
      transform: scale(1.1);
      border-color: #ffffff;
    }

    .color-swatch.selected {
      border-color: #4CAF50;
      box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.3);
    }

    .color-swatch.selected::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: #ffffff;
      font-weight: bold;
      text-shadow: 0 0 2px rgba(0, 0, 0, 0.8);
    }

    .custom-color-section {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
      padding: 15px;
      background-color: rgba(255, 255, 255, 0.05);
      border-radius: 6px;
      flex-wrap: wrap;
    }

    .custom-color-section label {
      color: #ffffff;
      font-size: 14px;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .custom-color-input {
      width: 40px;
      height: 40px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      background: none;
      flex-shrink: 0;
    }

    .custom-color-input::-webkit-color-swatch-wrapper {
      padding: 0;
    }

    .custom-color-input::-webkit-color-swatch {
      border: none;
      border-radius: 4px;
    }

    .custom-color-value {
      color: #cccccc;
      font-family: monospace;
      font-size: 14px;
    }

    .color-picker-footer {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      flex-wrap: wrap;
    }

    .select-btn, .cancel-btn {
      padding: 10px 20px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s;
      min-width: 80px;
      flex: 1;
      max-width: 120px;
    }

    .select-btn {
      background-color: #4CAF50;
      color: #ffffff;
    }

    .select-btn:hover:not(:disabled) {
      background-color: #45a049;
    }

    .select-btn:disabled {
      background-color: #666;
      cursor: not-allowed;
    }

    .cancel-btn {
      background-color: #666;
      color: #ffffff;
    }

    .cancel-btn:hover {
      background-color: #555;
    }

    // History Tab Styles
    .history-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
      padding: 0 5px;
    }

    .history-header span {
      color: #ffffff;
      font-weight: 500;
      font-size: 14px;
    }

    .clear-history-btn {
      background: none;
      border: 1px solid #666;
      color: #cccccc;
      padding: 4px 8px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      transition: all 0.2s;
    }

    .clear-history-btn:hover {
      background-color: #666;
      color: #ffffff;
      border-color: #888;
    }

    .no-history {
      text-align: center;
      padding: 30px 20px;
      color: #888;
    }

    .no-history p {
      margin: 0;
      font-size: 14px;
      line-height: 1.4;
    }

    /* Responsive Design - Mobile First Approach */
    
    /* Small Mobile Devices (320px and up) */
    @media (max-width: 480px) {
      .color-picker-overlay {
        padding: 10px;
        align-items: flex-start;
        padding-top: 20px;
      }

      .color-picker-modal {
        padding: 15px;
        min-width: 280px;
        max-height: 95vh;
        margin: 0 auto;
      }

      .color-picker-title {
        font-size: 16px;
      }

      .color-tabs {
        gap: 2px;
      }

      .tab-btn {
        padding: 8px 12px;
        font-size: 13px;
        min-width: 70px;
      }

      .color-swatch {
        width: 28px;
        height: 28px;
      }

      .color-row {
        gap: 6px;
        justify-content: center;
      }

      .custom-color-section {
        padding: 12px;
        gap: 8px;
      }

      .custom-color-section label {
        font-size: 13px;
      }

      .custom-color-input {
        width: 36px;
        height: 36px;
      }

      .color-picker-footer {
        gap: 8px;
      }

      .select-btn, .cancel-btn {
        padding: 12px 16px;
        font-size: 13px;
        min-width: 70px;
        max-width: none;
        flex: 1;
      }

      .history-header {
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
      }

      .clear-history-btn {
        align-self: flex-end;
      }
    }

    /* Medium Mobile Devices (481px - 768px) */
    @media (min-width: 481px) and (max-width: 768px) {
      .color-picker-overlay {
        padding: 15px;
      }

      .color-picker-modal {
        padding: 18px;
        min-width: 350px;
      }

      .color-swatch {
        width: 30px;
        height: 30px;
      }

      .color-row {
        gap: 7px;
      }

      .tab-btn {
        padding: 9px 14px;
        font-size: 13px;
      }
    }

    /* Tablet Devices (769px - 1024px) */
    @media (min-width: 769px) and (max-width: 1024px) {
      .color-picker-modal {
        padding: 20px;
        min-width: 400px;
        max-width: 600px;
      }

      .color-swatch {
        width: 32px;
        height: 32px;
      }

      .color-row {
        gap: 8px;
      }
    }

    /* Desktop Devices (1025px and up) */
    @media (min-width: 1025px) {
      .color-picker-modal {
        padding: 25px;
        min-width: 450px;
        max-width: 650px;
      }

      .color-swatch {
        width: 36px;
        height: 36px;
      }

      .color-row {
        gap: 10px;
      }

      .tab-btn {
        padding: 12px 20px;
        font-size: 15px;
      }

      .color-picker-title {
        font-size: 20px;
      }

      .custom-color-section {
        padding: 20px;
      }
    }

    /* Large Desktop Devices (1440px and up) */
    @media (min-width: 1440px) {
      .color-picker-modal {
        padding: 30px;
        min-width: 500px;
        max-width: 700px;
      }

      .color-swatch {
        width: 40px;
        height: 40px;
      }

      .color-row {
        gap: 12px;
      }
    }

    /* Landscape Mobile Orientation */
    @media (max-width: 768px) and (orientation: landscape) {
      .color-picker-overlay {
        align-items: flex-start;
        padding-top: 10px;
      }

      .color-picker-modal {
        max-height: 85vh;
        padding: 15px;
      }

      .color-tabs {
        margin-bottom: 15px;
      }

      .color-palette {
        margin-bottom: 15px;
      }
    }

    /* High DPI Displays */
    @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
      .color-swatch {
        border-width: 1px;
      }

      .tab-btn {
        border-bottom-width: 1px;
      }
    }

    /* Touch Device Optimizations */
    @media (hover: none) and (pointer: coarse) {
      .color-swatch {
        min-width: 44px;
        min-height: 44px;
      }

      .tab-btn {
        min-height: 44px;
      }

      .select-btn, .cancel-btn {
        min-height: 44px;
      }

      .clear-history-btn {
        min-height: 36px;
        min-width: 60px;
      }

      /* Improve touch scrolling */
      .color-picker-modal {
        -webkit-overflow-scrolling: touch;
      }
    }

    /* Print Styles */
    @media print {
      .color-picker-overlay {
        position: static;
        background: none;
      }

      .color-picker-modal {
        box-shadow: none;
        border: 1px solid #ccc;
        max-height: none;
      }
    }

    /* Accessibility Improvements */
    @media (prefers-reduced-motion: reduce) {
      .color-swatch,
      .tab-btn,
      .select-btn,
      .cancel-btn,
      .clear-history-btn {
        transition: none;
      }
    }
  `]
})
export class ColorPickerComponent implements OnInit {
  title: string = 'Pick a color!';
  description?: string;
  initialColor?: string;

  activeTab: 'primary' | 'accent' | 'history' = 'primary';
  selectedColor: string = '';
  customColor: string = '#000000';
  recentColors: string[] = [];
  maxHistorySize: number = 20;

  // Predefined color palettes
  primaryColors: ColorOption[] = [
    { code: '#FF6B35', name: 'Red Orange' },
    { code: '#8B5CF6', name: 'Purple' },
    { code: '#3B82F6', name: 'Blue' },
    { code: '#06B6D4', name: 'Cyan' },
    { code: '#10B981', name: 'Emerald' },
    { code: '#84CC16', name: 'Lime' },
    { code: '#F59E0B', name: 'Amber' },
    { code: '#EF4444', name: 'Red' },
    { code: '#EC4899', name: 'Pink' },
    { code: '#8B5A2B', name: 'Brown' },
    { code: '#6B7280', name: 'Gray' },
    { code: '#000000', name: 'Black' }
  ];

  primaryColors2: ColorOption[] = [
    { code: '#87CEEB', name: 'Sky Blue' },
    { code: '#F5F5DC', name: 'Beige' }
  ];

  accentColors: ColorOption[] = [
    { code: '#FFFFFF', name: 'White' },
    { code: '#FFE4E1', name: 'Misty Rose' },
    { code: '#FFDAB9', name: 'Peach' },
    { code: '#FFE4B5', name: 'Moccasin' },
    { code: '#F0E68C', name: 'Khaki' },
    { code: '#E6E6FA', name: 'Lavender' },
    { code: '#F0F8FF', name: 'Alice Blue' },
    { code: '#F5F5F5', name: 'White Smoke' },
    { code: '#FFF8DC', name: 'Cornsilk' },
    { code: '#FDF5E6', name: 'Old Lace' }
  ];

  constructor(
    private dialogRef: NbDialogRef<ColorPickerComponent>,
    @Inject(NB_DIALOG_CONFIG) private config: any
  ) {
    if (this.config) {
      this.title = this.config.title || this.title;
      this.description = this.config.description || this.description;
      this.initialColor = this.config.initialColor || this.initialColor;
    }
    if (this.initialColor) {
      this.selectedColor = this.initialColor;
      this.customColor = this.initialColor;
    }
  }

  ngOnInit(): void {
    // Load color history from localStorage
    this.loadHistoryFromStorage();
    
    // Add mobile-specific optimizations
    this.addMobileOptimizations();
  }

  setActiveTab(tab: 'primary' | 'accent' | 'history'): void {
    this.activeTab = tab;
  }

  selectColor(colorCode: string): void {
    this.selectedColor = colorCode;
    this.customColor = colorCode;
    this.addToHistory(colorCode);
  }

  confirmSelection(): void {
    if (this.selectedColor) {
      this.dialogRef.close({ status: true, color: this.selectedColor });
    }
  }

  closePicker(): void {
    this.dialogRef.close({ status: false });
  }

  // History Management Methods
  addToHistory(colorCode: string): void {
    // Remove the color if it already exists to avoid duplicates
    this.recentColors = this.recentColors.filter(color => color !== colorCode);
    
    // Add the new color to the beginning of the array
    this.recentColors.unshift(colorCode);
    
    // Limit the history size
    if (this.recentColors.length > this.maxHistorySize) {
      this.recentColors = this.recentColors.slice(0, this.maxHistorySize);
    }
    
    // Save to localStorage
    this.saveHistoryToStorage();
  }

  loadHistoryFromStorage(): void {
    try {
      const stored = localStorage.getItem('colorPickerHistory');
      if (stored) {
        this.recentColors = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load color history from localStorage:', error);
      this.recentColors = [];
    }
  }

  saveHistoryToStorage(): void {
    try {
      localStorage.setItem('colorPickerHistory', JSON.stringify(this.recentColors));
    } catch (error) {
      console.warn('Failed to save color history to localStorage:', error);
    }
  }

  clearHistory(): void {
    this.recentColors = [];
    this.saveHistoryToStorage();
  }

  // Mobile-specific optimizations
  private addMobileOptimizations(): void {
    // Check if device is mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Add mobile-specific class for additional styling
      const modal = document.querySelector('.color-picker-modal');
      if (modal) {
        modal.classList.add('mobile-device');
      }
      
      // Optimize for mobile viewport
      this.maxHistorySize = 15; // Reduce history size on mobile for better performance
    }
  }
} 